/**
 * Low-level Caddy admin UI. Drives /api/admin/caddy/* — snippet files, the main
 * Caddyfile, the live Admin API JSON config, and reverse-proxy upstreams.
 */
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type {
  CaddyApiResult, CaddySnippetFile, CaddyStatus, CaddyWriteResult,
} from '@hub/shared';
import { ApiError, api } from '../../api/client';
import {
  ActionButton, Alert, Card, CodeEditor, EmptyRow, Loading, Pill,
  fmtAgo, fmtBytes, useFlash,
} from '../../components/ui';

const BASE = '/api/admin/caddy';

type TabId = 'files' | 'caddyfile' | 'config' | 'upstreams';

const TABS: { id: TabId; label: string }[] = [
  { id: 'files', label: 'Site files' },
  { id: 'caddyfile', label: 'Caddyfile' },
  { id: 'config', label: 'Live JSON config' },
  { id: 'upstreams', label: 'Upstreams' },
];

/** The JSON config and upstreams come from the Admin API; the file tabs do not. */
function needsAdminApi(tab: TabId): boolean {
  return tab === 'config' || tab === 'upstreams';
}

function errText(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

/** A rejected write is reverted server-side, so say so rather than just failing. */
function writeErrText(err: unknown): string {
  const prefix = err instanceof ApiError && err.validation ? 'Rejected by Caddy (file reverted): ' : '';
  return prefix + errText(err);
}

function prettyBody(res: CaddyApiResult | null, fallback: string): string {
  if (!res) return fallback;
  if (res.body !== null && res.body !== undefined) return JSON.stringify(res.body, null, 2);
  return res.raw || fallback;
}

const toolbarStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: '0.75rem',
  flexWrap: 'wrap', marginBottom: '0.75rem',
};
const noteStyle: React.CSSProperties = { fontSize: '0.78rem', marginBottom: '0.75rem' };
const editorBarStyle: React.CSSProperties = {
  marginTop: '0.75rem', display: 'flex', gap: '0.5rem',
  alignItems: 'center', flexWrap: 'wrap',
};

function TabError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <>
      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', marginBottom: '1rem' }}>
        <button type="button" className="btn-ghost btn-sm" onClick={onRetry}>Retry</button>
      </div>
      <Alert tone="error">{message}</Alert>
    </>
  );
}

// ---------------------------------------------------------------------------
// Shared editor actions (Validate / Format)
// ---------------------------------------------------------------------------

function EditorActions({ content, onFormatted, show }: {
  content: string;
  onFormatted: (next: string) => void;
  show: (text: string, tone?: 'ok' | 'error' | 'warn') => void;
}) {
  const validate = useCallback(async () => {
    try {
      // An invalid config is a 200 with valid:false, not an error.
      const r = await api.post<{ ok: boolean; valid: boolean; error?: string }>(`${BASE}/validate`, { content });
      if (r.valid) show('Configuration is valid', 'ok');
      else show(r.error || 'Configuration is not valid', 'error');
    } catch (err) {
      show(errText(err), 'error');
      throw err;
    }
  }, [content, show]);

  const format = useCallback(async () => {
    try {
      const r = await api.post<{ ok: boolean; content: string }>(`${BASE}/format`, { content });
      onFormatted(r.content);
    } catch (err) {
      show(errText(err), 'error');
      throw err;
    }
  }, [content, onFormatted, show]);

  return (
    <>
      <ActionButton busyLabel="Checking…" onClick={validate}>Validate</ActionButton>
      <ActionButton busyLabel="Formatting…" onClick={format}>Format</ActionButton>
    </>
  );
}

// ---------------------------------------------------------------------------
// Site files
// ---------------------------------------------------------------------------

interface FileMeta { content: string; hubManaged: boolean; managedReason?: string }

/** `fileName === null` opens the new-file form. */
function FileEditor({ fileName, onClose, onSaved }: {
  fileName: string | null;
  onClose: () => void;
  onSaved: (name: string, res: CaddyWriteResult) => void;
}) {
  const isNew = fileName === null;
  const [name, setName] = useState('');
  const [content, setContent] = useState('');
  const [meta, setMeta] = useState<{ hubManaged: boolean; managedReason?: string }>({ hubManaged: false });
  const [loading, setLoading] = useState(!isNew);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const flash = useFlash();
  const { show } = flash;

  useEffect(() => {
    if (fileName === null) return;
    let cancelled = false;
    setLoading(true);
    setLoadErr(null);
    api.get<FileMeta>(`${BASE}/files/${encodeURIComponent(fileName)}`)
      .then((r) => {
        if (cancelled) return;
        setContent(r.content);
        setMeta({ hubManaged: r.hubManaged, managedReason: r.managedReason });
      })
      .catch((err: unknown) => { if (!cancelled) setLoadErr(errText(err)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [fileName]);

  const save = useCallback(async () => {
    const target = (fileName ?? name).trim();
    if (!target) {
      show('A file name is required', 'error');
      return;
    }
    try {
      const res = await api.put<CaddyWriteResult>(
        `${BASE}/files/${encodeURIComponent(target)}`, { content },
      );
      onSaved(target, res);
    } catch (err) {
      show(writeErrText(err), 'error');
      throw err;
    }
  }, [content, fileName, name, onSaved, show]);

  return (
    <div style={{ borderTop: '1px solid var(--ha-border)', paddingTop: '1rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
        <strong style={{ color: 'var(--ha-text-primary)', fontSize: '0.9rem' }}>
          {isNew ? 'New site file' : fileName}
        </strong>
        {isNew && (
          <input
            className="form-input mono"
            placeholder="example.caddy"
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={{ width: '16rem', padding: '0.25rem 0.5rem', fontSize: '0.8rem' }}
          />
        )}
        <button type="button" className="btn-ghost btn-sm" style={{ marginLeft: 'auto' }} onClick={onClose}>
          Close
        </button>
      </div>

      {loadErr && <Alert tone="error">{loadErr}</Alert>}
      {loading && <Loading label={`Loading ${fileName}…`} />}

      {!loading && !loadErr && (
        <>
          {meta.hubManaged && (
            <div style={{ marginBottom: '0.75rem' }}>
              <Alert tone="warn">
                {`The hub generates this file (${meta.managedReason || ''}). `}
                You can edit it, but it is rewritten the next time that instance changes.
              </Alert>
            </div>
          )}
          <CodeEditor value={content} onChange={setContent} rows={18} />
          <div style={editorBarStyle}>
            <ActionButton className="btn-primary btn-sm" busyLabel="Saving…" onClick={save}>
              Save &amp; reload
            </ActionButton>
            <EditorActions content={content} onFormatted={setContent} show={show} />
            {flash.node}
          </div>
        </>
      )}
    </div>
  );
}

function FilesTab({ confDir }: { confDir: string }) {
  const [files, setFiles] = useState<CaddySnippetFile[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editor, setEditor] = useState<{ name: string | null } | null>(null);
  // Lives above the table so a successful write's confirmation survives the refetch.
  const flash = useFlash();
  const { show } = flash;

  const load = useCallback(async () => {
    setError(null);
    try {
      const r = await api.get<{ files: CaddySnippetFile[] }>(`${BASE}/files`);
      setFiles(r.files);
    } catch (err) {
      setError(errText(err));
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const onSaved = useCallback((name: string, res: CaddyWriteResult) => {
    if (res.reloaded) show(`${name} saved and reloaded`, 'ok');
    else show(`${name} saved, but reload failed: ${res.output}`, 'warn');
    setEditor({ name });
    void load();
  }, [load, show]);

  const remove = useCallback(async (name: string) => {
    try {
      const res = await api.delete<CaddyWriteResult>(`${BASE}/files/${encodeURIComponent(name)}`);
      if (res.reloaded) show(`${name} deleted and reloaded`, 'ok');
      else show(`${name} deleted, but reload failed: ${res.output}`, 'warn');
      setEditor(null);
      void load();
    } catch (err) {
      show(errText(err), 'error');
      throw err;
    }
  }, [load, show]);

  const toggle = useCallback((name: string) => {
    setEditor((cur) => (cur && cur.name === name ? null : { name }));
  }, []);

  if (error) return <TabError message={error} onRetry={() => void load()} />;
  if (!files) return <Loading />;

  return (
    <>
      <div style={{ ...toolbarStyle, marginBottom: '1rem' }}>
        <button type="button" className="btn-ghost btn-sm" onClick={() => void load()}>Refresh</button>
        <button type="button" className="btn-ghost btn-sm" onClick={() => setEditor({ name: null })}>
          ＋ New site file
        </button>
        <span style={{ marginLeft: 'auto' }}>{flash.node}</span>
      </div>

      <p className="muted" style={noteStyle}>
        Snippet files in <code className="mono">{confDir}</code>, imported by the Caddyfile.
        Saving validates the whole configuration first and reverts the file if Caddy rejects it.
      </p>

      <div className="scroll-x">
        <table className="data">
          <thead>
            <tr>
              <th>File</th><th>Size</th><th>Modified</th><th /><th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {files.length === 0 && <EmptyRow colSpan={5}>No .caddy files in this directory.</EmptyRow>}
            {files.map((f) => (
              <tr key={f.name}>
                <td className="mono" style={{ color: 'var(--ha-text-primary)' }}>{f.name}</td>
                <td className="muted">{fmtBytes(f.sizeBytes)}</td>
                <td className="muted">{fmtAgo(f.modifiedAt)}</td>
                <td>
                  {f.hubManaged && (
                    <Pill tone="emerald" title={f.managedReason || ''}>hub-managed</Pill>
                  )}
                </td>
                <td style={{ display: 'flex', gap: '0.25rem' }}>
                  <button type="button" className="btn-ghost btn-sm" onClick={() => toggle(f.name)}>Edit</button>
                  {f.hubManaged ? (
                    <button type="button" className="btn-ghost btn-sm" disabled title={f.managedReason || ''}>
                      Delete
                    </button>
                  ) : (
                    <ActionButton
                      danger
                      busyLabel="Deleting…"
                      confirm={`Delete ${f.name}? Its routes stop being served after the reload.`}
                      onClick={() => remove(f.name)}
                    >
                      Delete
                    </ActionButton>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editor && (
        <div style={{ marginTop: '1.25rem' }}>
          <FileEditor
            key={editor.name ?? ' new'}
            fileName={editor.name}
            onClose={() => setEditor(null)}
            onSaved={onSaved}
          />
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Caddyfile
// ---------------------------------------------------------------------------

function CaddyfileTab({ configPath }: { configPath: string }) {
  const [content, setContent] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const flash = useFlash();
  const { show } = flash;

  const load = useCallback(async () => {
    setError(null);
    setContent(null);
    try {
      const r = await api.get<{ content: string }>(`${BASE}/caddyfile`);
      setContent(r.content);
    } catch (err) {
      setError(errText(err));
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const save = useCallback(async () => {
    try {
      const res = await api.put<CaddyWriteResult>(`${BASE}/caddyfile`, { content: content ?? '' });
      if (res.reloaded) show('Saved and reloaded', 'ok');
      else show(`Saved, but reload failed: ${res.output}`, 'warn');
    } catch (err) {
      show(writeErrText(err), 'error');
      throw err;
    }
  }, [content, show]);

  if (error) return <TabError message={error} onRetry={() => void load()} />;
  if (content === null) return <Loading />;

  return (
    <>
      <div style={toolbarStyle}>
        <button type="button" className="btn-ghost btn-sm" onClick={() => void load()}>Reload from disk</button>
        <span className="muted mono" style={{ fontSize: '0.78rem' }}>{configPath}</span>
      </div>
      <CodeEditor value={content} onChange={setContent} rows={24} />
      <div style={editorBarStyle}>
        <ActionButton
          className="btn-primary btn-sm"
          busyLabel="Saving…"
          confirm="Overwrite the main Caddyfile? It is validated first and reverted if Caddy rejects it."
          onClick={save}
        >
          Save &amp; reload
        </ActionButton>
        <EditorActions content={content} onFormatted={setContent} show={show} />
        {flash.node}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Live JSON config
// ---------------------------------------------------------------------------

const METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'] as const;
type Method = typeof METHODS[number];

function ConfigTab({ adminApiUrl }: { adminApiUrl: string }) {
  const [result, setResult] = useState<CaddyApiResult | null>(null);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [method, setMethod] = useState<Method>('GET');
  const [path, setPath] = useState('/config/');
  const [body, setBody] = useState('');
  const flash = useFlash();
  const { show } = flash;

  const load = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      setResult(await api.get<CaddyApiResult>(`${BASE}/config`));
      setSent(false);
    } catch (err) {
      setError(errText(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const send = useCallback(async () => {
    const trimmed = body.trim();
    if (method !== 'GET' && method !== 'DELETE' && !trimmed) {
      show(`${method} needs a JSON body`, 'error');
      return;
    }
    try {
      const r = await api.post<CaddyApiResult>(`${BASE}/config`, {
        method, path: path.trim(), body: trimmed || undefined,
      });
      setResult(r);
      setSent(true);
      if (!r.ok) throw new Error(`Caddy returned HTTP ${r.status}`);
    } catch (err) {
      show(errText(err), 'error');
      throw err;
    }
  }, [body, method, path, show]);

  if (error) return <TabError message={error} onRetry={() => void load()} />;
  if (loading) return <Loading />;

  return (
    <>
      <p className="muted" style={noteStyle}>
        The running configuration from Caddy&apos;s Admin API at <code className="mono">{adminApiUrl}</code>.
        Changes here apply immediately and are lost on restart unless they are also written to the Caddyfile.
      </p>

      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
        <select
          className="form-input"
          value={method}
          onChange={(e) => setMethod(e.target.value as Method)}
          style={{ width: '7rem', padding: '0.25rem 0.5rem', fontSize: '0.8rem' }}
        >
          {METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <input
          className="form-input mono"
          value={path}
          onChange={(e) => setPath(e.target.value)}
          style={{ flex: 1, minWidth: '14rem', padding: '0.25rem 0.5rem', fontSize: '0.8rem' }}
        />
        <ActionButton className="btn-primary btn-sm" busyLabel="Sending…" onClick={send}>Send</ActionButton>
        {flash.node}
      </div>

      <label className="muted" style={{ fontSize: '0.78rem', display: 'block', marginBottom: '0.25rem' }}>
        Request body (JSON — used by POST/PUT/PATCH)
      </label>
      <CodeEditor value={body} onChange={setBody} rows={6} />

      <label className="muted" style={{ fontSize: '0.78rem', display: 'block', margin: '0.75rem 0 0.25rem' }}>
        Response
        {sent && result && (
          <span style={{
            marginLeft: '0.5rem',
            color: result.ok ? 'var(--ha-accent-emerald)' : 'var(--ha-accent-red)',
          }}>
            HTTP {result.status}
          </span>
        )}
      </label>
      <pre className="output" style={{ maxHeight: '26rem' }}>
        {prettyBody(result, sent ? '(empty response)' : '(no response yet)')}
      </pre>
    </>
  );
}

// ---------------------------------------------------------------------------
// Upstreams
// ---------------------------------------------------------------------------

interface Upstream { address: string; num_requests?: number; fails?: number }

function UpstreamsTab() {
  const [result, setResult] = useState<CaddyApiResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    setResult(null);
    try {
      setResult(await api.get<CaddyApiResult>(`${BASE}/upstreams`));
    } catch (err) {
      setError(errText(err));
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  if (error) return <TabError message={error} onRetry={() => void load()} />;
  if (!result) return <Loading />;

  const list: Upstream[] = Array.isArray(result.body) ? (result.body as Upstream[]) : [];

  return (
    <>
      <div style={toolbarStyle}>
        <button type="button" className="btn-ghost btn-sm" onClick={() => void load()}>Refresh</button>
        <span className="muted" style={{ fontSize: '0.78rem' }}>Backends Caddy is currently proxying to.</span>
      </div>
      <div className="scroll-x">
        <table className="data">
          <thead>
            <tr><th>Address</th><th>Active requests</th><th>Fails</th><th>Status</th></tr>
          </thead>
          <tbody>
            {list.length === 0 && <EmptyRow colSpan={4}>No upstreams reported.</EmptyRow>}
            {list.map((u) => {
              const healthy = u.num_requests !== undefined && !u.fails;
              return (
                <tr key={u.address}>
                  <td className="mono" style={{ color: 'var(--ha-text-primary)' }}>{u.address}</td>
                  <td className="muted">{u.num_requests}</td>
                  <td style={{ color: u.fails ? 'var(--ha-accent-red)' : 'var(--ha-text-muted)' }}>{u.fails}</td>
                  <td style={{ color: healthy ? 'var(--ha-accent-emerald)' : 'var(--ha-text-muted)' }}>
                    {healthy ? '● healthy' : '● unknown'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function AdminCaddyPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState<CaddyStatus | null>(null);
  const [statusErr, setStatusErr] = useState<string | null>(null);
  const flash = useFlash();
  const { show } = flash;

  const loadStatus = useCallback(async () => {
    setStatusErr(null);
    try {
      setStatus(await api.get<CaddyStatus>(`${BASE}/status`));
    } catch (err) {
      setStatus(null);
      setStatusErr(errText(err));
    }
  }, []);

  useEffect(() => { void loadStatus(); }, [loadStatus]);

  const hash = location.hash.replace(/^#/, '');
  const active: TabId = TABS.some((t) => t.id === hash) ? (hash as TabId) : 'files';

  // Keep the URL canonical so every tab stays deep-linkable.
  useEffect(() => {
    if (location.hash !== `#${active}`) {
      navigate({ pathname: location.pathname, search: location.search, hash: active }, { replace: true });
    }
  }, [active, location.hash, location.pathname, location.search, navigate]);

  const reload = useCallback(async () => {
    try {
      await api.post<{ ok: boolean; output: string }>(`${BASE}/reload`);
      show('Caddy reloaded', 'ok');
    } catch (err) {
      show(errText(err), 'error');
      throw err;
    }
  }, [show]);

  const enabled = (tab: TabId) => !needsAdminApi(tab) || !!status?.adminApiReachable;

  return (
    <>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1rem',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <h2 style={{ color: 'var(--ha-text-primary)', fontSize: '1.25rem', fontWeight: 600, margin: 0 }}>Caddy</h2>
          {status && (
            <>
              <Pill tone="muted">
                {status.binaryAvailable ? (status.version || 'caddy') : 'caddy not installed'}
              </Pill>
              <Pill tone={status.adminApiReachable ? 'emerald' : 'muted'}>
                {status.adminApiReachable ? 'admin API up' : 'admin API down'}
              </Pill>
            </>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          {status && (
            <span
              className="muted"
              title={`conf.d: ${status.confDir}\nCaddyfile: ${status.configPath}`}
              style={{
                fontSize: '0.8rem', maxWidth: '26rem', overflow: 'hidden',
                textOverflow: 'ellipsis', whiteSpace: 'nowrap', direction: 'rtl',
              }}
            >
              {status.confDir}
            </span>
          )}
          <ActionButton busyLabel="Reloading…" onClick={reload}>Reload Caddy</ActionButton>
          {flash.node}
        </div>
      </div>

      {statusErr && (
        <div style={{ marginBottom: '1rem' }}>
          <Alert tone="error">
            <span>{statusErr}</span>
            <button
              type="button"
              className="btn-ghost btn-sm"
              style={{ marginLeft: '0.75rem' }}
              onClick={() => void loadStatus()}
            >
              Retry
            </button>
          </Alert>
        </div>
      )}

      {status && !status.validationAvailable && (
        <div style={{ marginBottom: '1rem' }}>
          <Alert tone="warn">
            {status.binaryAvailable
              ? `The Caddyfile at ${status.configPath} does not exist, so edits cannot be validated before saving.`
              : 'The caddy binary is not available on this host: edits cannot be validated, formatted, or reloaded.'}
          </Alert>
        </div>
      )}

      <div style={{
        display: 'flex', gap: '0.25rem',
        borderBottom: '1px solid var(--ha-border)', marginBottom: '1.5rem',
      }}>
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`tab-btn${t.id === active ? ' tab-btn-active' : ''}`}
            disabled={!enabled(t.id)}
            title={enabled(t.id) ? undefined : 'Caddy Admin API is not reachable'}
            onClick={() => navigate({ pathname: location.pathname, search: location.search, hash: t.id })}
          >
            {t.label}
          </button>
        ))}
      </div>

      {status && (
        <Card>
          {!enabled(active) ? (
            <Alert tone="warn">
              {`Caddy's Admin API at ${status.adminApiUrl} is not reachable, so the live configuration `}
              is unavailable. The file tabs still work.
            </Alert>
          ) : active === 'files' ? (
            <FilesTab confDir={status.confDir} />
          ) : active === 'caddyfile' ? (
            <CaddyfileTab configPath={status.configPath} />
          ) : active === 'config' ? (
            <ConfigTab adminApiUrl={status.adminApiUrl} />
          ) : (
            <UpstreamsTab />
          )}
        </Card>
      )}
    </>
  );
}
